import { getSelectedCampaignBrands } from '@/components/AccelerationForm/utils/brandForApi'
import {
  DETAIL_RULE_TEXT_MAX,
  DETAIL_STEP_TEXT_MAX,
  MAX_DETAIL_RULES,
  MAX_DETAIL_STEPS,
  PAGE_DESCRIPTION_MAX,
  PAGE_TITLE_MAX
} from '@/constants/accelerationForm'
import { ACCELERATION_STEPPER_VARIANT } from '@/constants/accelerationStepper'
import { CAMPAIGN_TYPE, POINTS_TYPE, POINTS_VALIDITY_TYPE } from '@/constants/accelerationTable'
import { stripHtmlTags } from '@/utils/htmlHelpers/htmlHelpers'

import type {
  AccelerationContentDetails,
  AccelerationContentFields,
  AccelerationFormState,
  AccelerationStepperVariant,
  CampaignBrandFlags,
  ContentByBrandForm
} from '../AccelerationForm.types'

export const plainTextFromHtml = (html: string): string => stripHtmlTags(html)

const isFlexiblePointsRule = (pointsType: string) =>
  [POINTS_TYPE.TIER_RULE, POINTS_TYPE.DAYS_CALENDAR].includes(
    pointsType as typeof POINTS_TYPE.TIER_RULE | typeof POINTS_TYPE.DAYS_CALENDAR
  )

const isValidCountOfDays = (value: string): boolean => {
  if (!/^\d+$/.test(value)) return false
  const parsedValue = Number.parseInt(value, 10)
  return Number.isInteger(parsedValue) && parsedValue >= 1
}

const hasValidPointsValidity = (values: AccelerationFormState): boolean => {
  const { pointsValidityType, points } = values

  if (!points.description || !points.type) return false

  if (pointsValidityType === POINTS_VALIDITY_TYPE.FIXED) {
    return points.type === POINTS_TYPE.DEFAULT && Boolean(points.startDate && points.endDate)
  }

  if (pointsValidityType === POINTS_VALIDITY_TYPE.FLEXIBLE) {
    if (!isFlexiblePointsRule(points.type)) return false
    if (points.type === POINTS_TYPE.DAYS_CALENDAR) return isValidCountOfDays(points.countOfDays)
    return true
  }

  return false
}

export const validateStepScoring = (values: AccelerationFormState): boolean => {
  const { segmentation, pointsValidityType, name, type, startDate, endDate } = values
  if (
    !segmentation.campaignType ||
    !segmentation.audienceType ||
    !pointsValidityType ||
    !name ||
    !type ||
    !startDate ||
    !endDate
  )
    return false
  if (!hasValidPointsValidity(values)) return false

  if (type === CAMPAIGN_TYPE.MULTIPLIER && !values.multiplier) return false
  if (type === CAMPAIGN_TYPE.POINT && !values.point) return false
  return true
}

export const validateStepDesign = (
  values: AccelerationContentFields,
  brand: CampaignBrandFlags,
  contentByBrand: ContentByBrandForm,
  variant: AccelerationStepperVariant
): boolean => {
  const { title, description, buttonText, buttonUrl, imageKey, imageUrl } = values
  const plainDescription = plainTextFromHtml(description).trim()
  const selectedBrands = getSelectedCampaignBrands(brand)

  const hasValidDestinationUrl =
    selectedBrands.length > 0
      ? selectedBrands.every((brandCode) => Boolean(contentByBrand[brandCode]?.buttonUrl?.trim()))
      : variant !== ACCELERATION_STEPPER_VARIANT.CREATE && Boolean(buttonUrl?.trim())

  if (!hasValidDestinationUrl) return false

  let isImageValid = false
  if (variant === ACCELERATION_STEPPER_VARIANT.EDIT) {
    isImageValid = Boolean(imageUrl?.trim())
  } else {
    isImageValid = Boolean(imageKey?.trim())
  }

  return Boolean(title && plainDescription && buttonText && isImageValid)
}

export const validateStepDetails = (values: AccelerationContentDetails): boolean => {
  const { pageTitle, pageDescription, steps, rules } = values
  if (!pageTitle || pageTitle.length > PAGE_TITLE_MAX) return false
  const plainPage = plainTextFromHtml(pageDescription)
  if (!plainPage.trim() || plainPage.length > PAGE_DESCRIPTION_MAX) return false
  if (steps.length > MAX_DETAIL_STEPS || rules.length > MAX_DETAIL_RULES) return false
  const stepStrings = steps.map((s) => plainTextFromHtml(s.html))
  const ruleStrings = rules.map((r) => plainTextFromHtml(r.html))
  if (!steps.length || stepStrings.some((s) => !s.trim())) return false
  if (!rules.length || ruleStrings.some((r) => !r.trim())) return false
  for (const s of stepStrings) {
    if (s.length > DETAIL_STEP_TEXT_MAX) return false
  }
  for (const r of ruleStrings) {
    if (r.length > DETAIL_RULE_TEXT_MAX) return false
  }
  return true
}
